import { useState } from 'react';

import apiToken, {
  handleApiError,
  isSuccess,
} from '@/shared/services/axiosInstance';
import Modal from '@/shared/components/UI/Modal';
import Button from '@/shared/components/UI/Button';
import Input from '@/shared/components/UI/Input';

import { AddressData, AddressRequest } from '../types/AddressTypes';

interface AddAddressModalProps {
  onClose: () => void;
  onAddressAdded?: () => void;
  editAddress?: AddressData; // Edit mode: existing address data
}

const AddAddressModal = ({
  onClose,
  onAddressAdded,
  editAddress,
}: AddAddressModalProps) => {
  const [addressInfo, setAddressInfo] = useState<AddressRequest>({
    postalCode: editAddress?.postalCode || '',
    addressDetail: editAddress?.addressDetail || '',
    isDefault: editAddress?.isDefault || false,
  });

  const isEditMode = !!editAddress;

  const handleInputChange = (
    field: keyof AddressRequest,
    value: string | boolean,
  ) => {
    setAddressInfo((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      let response;

      if (isEditMode) {
        // Edit mode: PATCH request
        response = await apiToken.patch(
          `/api/v1/user/address/${editAddress.addressUuid}`,
          addressInfo,
        );
      } else {
        // Add mode: POST request
        response = await apiToken.post('/api/v1/user/address', addressInfo);
      }

      if (isSuccess(response.data)) {
        alert(
          isEditMode
            ? 'Address updated successfully!'
            : 'Address added successfully!',
        );
        onAddressAdded?.();
        onClose();
      }
    } catch (error) {
      const errorMessage = handleApiError(error);
      console.error(
        `Failed to ${isEditMode ? 'update' : 'add'} address:`,
        errorMessage,
      );
      alert(errorMessage);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEditMode ? 'Edit Address' : 'Add Address'}
      size="md"
    >
      <div className="mt-6 flex gap-4 rounded-lg bg-[#f4ecf4] p-4">
        <img src="/images/alert-circle-line.svg" alt="x" />
        <div>
          <p className="text-left text-[12px] font-medium leading-[1.4] text-[#95699f]">
            When entering an address, use Korean in Korea, Chinese in China and
            Taiwan, and English in other countries.
          </p>
          <p className="text-left text-[12px] font-bold leading-[1.4] text-[#95699f]">
            We are not responsible for any delivery issues caused by incorrect
            address input.
          </p>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="mt-7 text-left">
        <div className="mb-5 flex flex-col">
          <Input
            label="Postal code"
            type="text"
            name="postal-code"
            value={addressInfo.postalCode}
            onChange={(e) => handleInputChange('postalCode', e.target.value)}
            placeholder="Postal code (optional)"
            className="h-9 text-[14px]"
          />
        </div>
        <div className="mb-5 flex flex-col">
          <Input
            label="Shipping address"
            type="text"
            name="shipping-address"
            value={addressInfo.addressDetail}
            onChange={(e) => handleInputChange('addressDetail', e.target.value)}
            placeholder="Shipping address"
            className="h-9 text-[14px]"
            required
          />
        </div>
        <label className="flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            checked={addressInfo.isDefault}
            onChange={(e) => handleInputChange('isDefault', e.target.checked)}
            className="h-4 w-4"
          />
          <p className="text-[14px] font-normal">Set as default address</p>
        </label>
        <Button
          type="submit"
          variant="primary"
          size="md"
          className="mt-8 w-full"
        >
          {isEditMode ? 'UPDATE' : 'SAVE'}
        </Button>
      </form>
    </Modal>
  );
};

export default AddAddressModal;
